import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import { categories, featuredComics, dailyUpdateComics } from '@/mocks/comics';
import type { ComicArticle } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  const allComics: ComicArticle[] = [...featuredComics, ...dailyUpdateComics];
  const filteredComics = allComics.filter((c) => c.categorySlug === slug);

  if (!category) {
    return (
      <>
        <SEO title="分类未找到 - 韩国漫画网" description="该分类不存在" />
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-3">分类未找到</h1>
            <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryTitle = `${category.name} - 韩国免费漫画`;
  const categoryDesc = `${category.description}。在韩国漫画网免费阅读${category.name}，下拉式全集在线阅读，每日更新，高清画质。`;
  const categoryKeywords = `韩国免费漫画,${category.name},韩漫大全,下拉式漫画,韩国漫画网`;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://bendionline.com/' },
      { '@type': 'ListItem', position: 2, name: category.name, item: 'http://bendionline.com/' },
    ],
  };

  return (
    <>
      <SEO
        title={categoryTitle}
        description={categoryDesc}
        keywords={categoryKeywords}
        canonicalPath={'/'}
        ogImage={category.coverImage}
        schema={breadcrumbSchema}
      />
      <Navbar />

      <main>
        <section className="relative w-full h-[360px] md:h-[480px] overflow-hidden">
          <img
            src={category.coverImage}
            alt={category.name}
            title={category.name}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/60"></div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4">
            <nav className="mb-4 text-xs md:text-sm text-white/70" aria-label="面包屑导航">
              <Link to="/" className="hover:text-white transition-colors whitespace-nowrap cursor-pointer">首页</Link>
              <span className="mx-2">/</span>
              <span className="text-white">{category.name}</span>
            </nav>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-3 md:mb-4">
              {category.name}
            </h1>
            <p className="text-sm md:text-base text-white/80 max-w-xl leading-relaxed">
              {category.description}
            </p>
          </div>
        </section>

        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-[1200px] mx-auto">
            <div className="mb-10 text-center md:text-left">
              <h2 className="text-2xl font-heading font-bold text-foreground-950">
                本类推荐
              </h2>
              <p className="text-sm text-foreground-600 mt-1">
                共 {filteredComics.length} 部作品
              </p>
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {filteredComics.map((comic) => (
                  <Link
                    key={comic.slug}
                    to="/"
                    className="group bg-background-50 rounded-xl overflow-hidden cursor-pointer transition-all hover:-translate-y-1"
                  >
                    <div className="relative w-full h-[320px] md:h-[360px]">
                      <img
                        src={comic.coverImage}
                        alt={comic.title}
                        title={comic.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="p-4 md:p-5 text-center">
                      <h3 className="text-sm md:text-base font-semibold text-foreground-950 mb-1.5">
                        {comic.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mb-3">
                        {comic.author} · 更新至第{comic.chapters[0]?.number || comic.totalChapters}话
                      </p>
                      <span className="inline-block w-full py-2.5 text-xs font-semibold rounded-full border border-foreground-300 text-foreground-700 hover:bg-primary-500 hover:text-white hover:border-primary-500 transition-colors whitespace-nowrap">
                        开始阅读
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-book-open-line text-4xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画，敬请期待</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}